import { useContext } from 'react'
import { classes, media, style, stylesheet } from 'typestyle'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'

/* Context ====================================================================================== */
import { formatDate } from '../../../share/format'
import { NavigationContext } from '../../../contexts/navigation'
import { getCurrentUser } from '../../../services/auth'

/* Components =================================================================================== */
import { Box } from '../../../components/box/box.component'
import { Heading } from '../../../components/text/heading.component'
import { Loader } from '../../../components/loader'
import { Text } from '../../../components/text/text.component'
import { Select } from '../../../components/select'
import Skeleton from '../../../components/skeleton'

/* Styles ======================================================================================= */
import { pt0 } from '../../../styles/styleset/padding/pt0'
import { f16i } from '../../../styles/styleset/font-size/f16i'
import {
  iconResponsive,
  paddingLeftInner,
  paddingVerticalOuter,
} from '../../../styles/default/padding'
import { pl4 } from '../../../styles/styleset/padding/pl4'
import { pr4 } from '../../../styles/styleset/padding/pr4'
import { pr8 } from '../../../styles/styleset/padding/pr8'
import { hidemd } from '../../../styles/styleset/hide/hidemd'
import { mt8 } from '../../../styles/styleset/margin/mt8'
import { hidei } from '../../../styles/styleset/hide/hidei'
import { flexmdi } from '../../../styles/styleset/flex/flexmdi'
import { ml4 } from '../../../styles/styleset/margin/ml4'
import { pl8 } from '../../../styles/styleset/padding/pl8'
import { mb4 } from '../../../styles/styleset/margin/mb4'
import { mt28 } from '../../../styles/styleset/margin/mt28'
import { p8 } from '../../../styles/styleset/padding/p8'

/* <BodyHead /> ================================================================================= */
export const BodyHead = (props: {
  subtitle?: string
  title: string
  description?: string
  actions?: {
    label: string
    to?: string
    icon?: any
    onClick?: any
    buttonProps?: any
    primary?: boolean
  }[]
  selectComponent?: { value?: any; options?: string[]; onChange: any }
  maxWidth?: any
  children?: any
  viewMode?: any
}) => {
  const { title, maxWidth = 960, viewMode } = props
  const { navigation } = useContext(NavigationContext)
  const { breadcrumbs = [] } = navigation.payload
  // const breadcrumbs: any = []
  const { currentUser } = getCurrentUser()

  return (
    <Box
      className={classes(
        pt0,
        style({
          ...media(
            { type: 'print' },
            {
              background: 'white !important',
            },
          ),
        }),
      )}
      baseline="background"
      fullWidth
      onlyContrast={false}
      relative
      row
    >
      {/* <Helmet>
        <title>
          {[
            process.env.REACT_APP_PROJECT_NAME,
            ...breadcrumbs
              .filter((a: any) => a.label !== 'My Groups')
              .map((item: any, key: number) => {
                return item.label
              }),
          ].join(' | ')}
        </title>
      </Helmet> */}
      <Box baseline="none" className={classes(paddingVerticalOuter, mt28)}>
        <Box baseline="none" maxWidth={maxWidth} fullWidth fullHeight centerX alignCenterY row>
          <Box baseline="none" className={classes(mb4)}>
            <Box baseline="none" row alignCenterY fullHeight fullWidth relative>
              <Box
                className={classes(mb4, paddingLeftInner)}
                baseline="none"
                relative
                flex
                alignCenterY
                spaceBetween
              >
                <Loader
                  timeout={0}
                  loaded={breadcrumbs.length > 0}
                  component={
                    <Box baseline="none" flex className={style({ flexWrap: 'wrap' })}>
                      {breadcrumbs.map((item: any, key: number) => {
                        return (
                          <Box
                            baseline="none"
                            key={key}
                            alignCenterY
                            flex
                            className={classes(
                              key === 0 &&
                                style({
                                  ...media(
                                    { type: 'print' },
                                    {
                                      display: 'none',
                                    },
                                  ),
                                }),
                            )}
                          >
                            <Link to={item.to}>
                              <Text
                                size="sm"
                                sublink
                                // link={key === breadcrumbs.length - 1}
                                shrink
                              >
                                {item.label}
                              </Text>
                            </Link>
                            {key < breadcrumbs.length - 1 && (
                              <span
                                className={classes(
                                  process.env.REACT_APP_GOOGLE_MATERIAL_ICONS,
                                  f16i,
                                  STYLES.iconBreadcrumbDivider,
                                )}
                              >
                                keyboard_arrow_right
                              </span>
                            )}
                          </Box>
                        )
                      })}
                    </Box>
                  }
                  skeleton={<Skeleton width={160} height={14} variant="text" />}
                />
                <Box className="print" baseline="none">
                  <Text baseline="surface" size="xs" light>
                    Printed by {currentUser?.fullName},{' '}
                    {formatDate(new Date().toUTCString(), { time: true })}
                  </Text>
                </Box>
              </Box>
              <Box
                className={paddingLeftInner}
                baseline="none"
                centerX
                fullWidth
                relative
                row
                alignCenterY
              >
                <Box baseline="none" column fullWidth alignCenterY spaceBetween>
                  <Loader
                    component={
                      <Heading baseline="background" size={'xl'} semiBold>
                        {title}
                      </Heading>
                    }
                    timeout={0}
                    loaded={title != '-'}
                    skeleton={<Skeleton width={200} height={35} variant="text" />}
                  />
                  <Box
                    className={classes(
                      hidemd,
                      style({
                        ...media(
                          { type: 'print' },
                          {
                            display: 'none',
                          },
                        ),
                      }),
                    )}
                    baseline="none"
                  >
                    {title != '-' && viewMode && viewMode?.options?.length > 1 && (
                      <Box baseline="surface" flex rounded alignCenterY shadow className={pl8}>
                        <Text size="xs" baseline="surface" lighter shrink semiBold>
                          View
                        </Text>
                        <Select
                          baseline="none"
                          buttonProps={{
                            style: {
                              padding: '0px 0px 0px 8px',
                            },
                          }}
                          flip
                          anchorPosition="bottom right"
                          dialogPosition="top right"
                          disableIcon={true}
                          renderOption={(data: any) => {
                            return (
                              <Box
                                baseline="none"
                                className={classes(
                                  p8,
                                  style({
                                    $nest: {
                                      '&:hover': {
                                        background: 'rgb(240,240,240)',
                                        cursor: 'pointer',
                                      },
                                    },
                                  }),
                                )}
                                style={{ whiteSpace: 'nowrap' }}
                                flex
                                alignCenterY
                                fullWidth
                              >
                                <Text size="sm" disableBreak>
                                  {data}
                                </Text>
                              </Box>
                            )
                          }}
                          renderValue={(data: any) => {
                            return (
                              <Box className={classes(pl4, pr4)} baseline="none" flex alignCenterY>
                                <Box flex alignCenterY baseline="none">
                                  {/* {data} */}
                                  <Text size="sm">{data}</Text>
                                  <i className={classes('material-icons', ml4, iconResponsive)}>
                                    keyboard_arrow_down
                                  </i>
                                </Box>
                              </Box>
                            )
                          }}
                          size="lg"
                          value={viewMode?.value}
                          options={viewMode?.options}
                          onChange={viewMode?.onChange}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box
                  className={classes(
                    hidei,
                    flexmdi,
                    mt8,
                    style({
                      ...media(
                        { type: 'print' },
                        {
                          display: 'none !important',
                        },
                      ),
                    }),
                  )}
                  baseline="none"
                  flex
                >
                  {title != '-' && viewMode && (
                    <Box baseline="surface" flex rounded alignCenterY shadow className={pl8}>
                      <Text size="xs" baseline="surface" lighter shrink semiBold>
                        View
                      </Text>
                      <Select
                        baseline="none"
                        buttonProps={{
                          style: {
                            padding: '0px 0px 0px 8px',
                          },
                        }}
                        flip
                        anchorPosition="bottom right"
                        dialogPosition="top right"
                        disableIcon={true}
                        renderOption={(data: any) => {
                          return (
                            <Box
                              baseline="none"
                              className={classes(
                                p8,
                                style({
                                  $nest: {
                                    '&:hover': {
                                      background: 'rgb(240,240,240)',
                                      cursor: 'pointer',
                                    },
                                  },
                                }),
                              )}
                              style={{ whiteSpace: 'nowrap' }}
                              flex
                              alignCenterY
                              fullWidth
                            >
                              <Text size="sm" disableBreak>
                                {data}
                              </Text>
                            </Box>
                          )
                        }}
                        renderValue={(data: any) => {
                          return (
                            <Box className={classes(pl4, pr4)} baseline="none" flex alignCenterY>
                              <Box flex alignCenterY baseline="none">
                                {/* {data} */}
                                <Text size="sm">{data}</Text>
                                <i className={classes('material-icons', ml4, iconResponsive)}>
                                  keyboard_arrow_down
                                </i>
                              </Box>
                            </Box>
                          )
                        }}
                        size="lg"
                        value={viewMode?.value}
                        options={viewMode?.options}
                        onChange={viewMode?.onChange}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          {/* <Line className={classes(mt8, mb8)} dark /> */}
        </Box>
      </Box>
    </Box>
  )
}

const STYLES = stylesheet({
  iconBreadcrumbDivider: {
    marginLeft: 4,
    marginRight: 4,
    marginBottom: 2,
  },
})
